/**
 * @fileoverview Per-section contextual help popover for the Section-editor builder mode.
 * @module ui-admin/components/SectionHelpPopover
 *
 * Responsibilities: - Render a floating help card anchored to a section row's "?" button, showing
 *                     what the section is, how it renders in the PDF, configuration tips, and a
 *                     "Full help →" deep link into the builder help panel.
 *                   - Close on outside click or Escape; position adjacent to the anchor element.
 * Dependencies:     - React, react-dom (portal), lucide-react; a caller-supplied `ComponentHelpEntry`
 *                     and label. Host-neutral — no document-render or band-kind coupling, so it works
 *                     for the Section editor in both tenant-web and system-web.
 * Architecture:     - Presentation-only. Content is static data passed in by the host — no API/Dexie,
 *                     no mock data. Positioned via getBoundingClientRect (no extra dependency).
 * Notes:            - One popover is open at a time; the parent owns the open/close state. Mirrors
 *                     the canvas `ComponentHelpPopover` so both builder modes give the same help.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, ArrowRight } from 'lucide-react';
import type { ComponentHelpEntry, HelpSectionId } from '../../help/types';

/**
 * @contract SectionHelpPopoverProps
 * Purpose: Control a document-section contextual help popover.
 * Fields:
 * - label: the section's display name (e.g. "Header").
 * - entry: the static help content for the section.
 * - anchorRef: ref to the element (the "?" button) the popover should sit beside.
 * - open: whether the popover is shown.
 * - onClose: called on outside click, Escape, or the close button.
 * - onOpenFullHelp: optional; called with the relevant section id by "Full help →".
 */
export interface SectionHelpPopoverProps {
  label: string;
  entry: ComponentHelpEntry;
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  onOpenFullHelp?: (section: HelpSectionId) => void;
}

const WIDTH = 320;

/**
 * @component SectionHelpPopover
 * Purpose:     Just-in-time help card for a single document section.
 * Props:       SectionHelpPopoverProps.
 * Behavior:    Positions beside the anchor; closes on outside click / Escape / ×; "Full help →"
 *              calls onOpenFullHelp with the section's mapped help-panel section.
 * Data Source: caller-supplied static content (no API/Dexie).
 * Constraints: no mock data; no network.
 */
export function SectionHelpPopover({ label, entry, anchorRef, open, onClose, onOpenFullHelp }: SectionHelpPopoverProps): React.ReactPortal | null {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return;
    const r = anchor.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    let left = r.left;
    if (left + WIDTH + 8 > vw) left = Math.max(8, vw - WIDTH - 8);
    setPos({ top: r.bottom + 6, left });
  }, [open, anchorRef]);

  const handleOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    if (cardRef.current?.contains(target)) return;
    if (anchorRef.current?.contains(target)) return;
    onClose();
  }, [onClose, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, handleOutside, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-label={`${label} help`}
      data-testid="section-help-popover"
      className="rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: WIDTH, zIndex: 40 }}
    >
      <div className="mb-2 flex items-center gap-2">
        <HelpCircle className="h-4 w-4 text-orange-500" aria-hidden="true" />
        <span className="text-sm font-bold text-gray-900">{label}</span>
        <span className="flex-1" />
        <button type="button" aria-label="Close help" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="mb-2 text-xs leading-relaxed text-gray-700">
        <span className="font-semibold text-gray-800">What it is. </span>{entry.whatItIs}
      </p>
      <p className="mb-2 text-xs leading-relaxed text-gray-700">
        <span className="font-semibold text-gray-800">Renders as. </span>{entry.rendersAs}
      </p>
      <ul className="mb-2 ml-4 list-disc space-y-0.5 text-xs text-gray-600">
        {entry.tips.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
      {onOpenFullHelp && (
        <button
          type="button"
          data-testid="section-help-full-link"
          onClick={() => { onClose(); onOpenFullHelp(entry.helpSection); }}
          className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
        >
          Full help <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>,
    document.body,
  );
}

export default SectionHelpPopover;
